/**
 * 車両表示順 (truck display order) の比較規則を集約する utility。
 *
 * API (app/controllers/api/v3/plannings/trucks_controller.rb) と一致させる:
 *   1. displayOrder が設定されている車両 (= TruckDisplayOrderVersion 経由で固定された車両) を先に出す
 *   2. bucket 内では displayOrder ?? sortOrder ?? MAX 昇順
 *   3. 同値の場合は id 昇順 (安定化用)
 *
 * Frontend で displayOrder ?? sortOrder だけで比較していると、
 *   - 履歴に含まれない新規車両の sortOrder が小さい → 履歴あり車両より前に並ぶ
 * という API との乖離が出るため、必ずこの util を使うこと。
 */

// app/controllers/api/v3/plannings/trucks_controller.rb の `2**16` と揃える。
// MAX_SAFE_INTEGER を使うと sortOrder > 65536 の車両が来た場合に API と順序が逆転するため、
// この値を必ず使うこと。
const FALLBACK_ORDER = 2 ** 16;

type TruckOrderKeyInput = {
  id: number;
  displayOrder?: number | null;
  sortOrder?: number | null;
};

export const truckDisplayOrderSortKey = (
  truck: TruckOrderKeyInput,
): [number, number, number] => {
  const hasDisplayOrder = truck.displayOrder != null;
  const value = truck.displayOrder ?? truck.sortOrder ?? FALLBACK_ORDER;
  return [hasDisplayOrder ? 0 : 1, value, truck.id];
};

export const compareTrucksByDisplayOrder = (
  a: TruckOrderKeyInput,
  b: TruckOrderKeyInput,
): number => {
  const ka = truckDisplayOrderSortKey(a);
  const kb = truckDisplayOrderSortKey(b);
  for (let i = 0; i < ka.length; i += 1) {
    if (ka[i] !== kb[i]) return ka[i] - kb[i];
  }
  return 0;
};

/**
 * 保存済みの表示順 (storedIds) を、現在の車両一覧 (currentTruckIds) に同期させる。
 *
 * 車両の追加・削除が同時に起きて件数が変わらないケースでも正しく検知するため、
 * 件数比較ではなく id の集合差分で判定する:
 *   1. storedIds のうち現存する車両の id だけを順序を保って残す (= 削除分を除去)
 *   2. currentTruckIds のうち storedIds に無い車両の id を末尾に追加 (= 追加分)
 *
 * 追加も削除も無い場合は storedIds の参照をそのまま返す。これにより呼び出し側は
 * 参照比較 (`!==`) だけで変更を検知でき、不要な再 setState / 無限ループを防げる。
 */
export const reconcileSortedTruckIds = (
  storedIds: number[],
  currentTruckIds: number[],
): number[] => {
  const currentSet = new Set(currentTruckIds);
  const storedSet = new Set(storedIds);
  const activeIds = storedIds.filter((id) => currentSet.has(id));
  const newIds = currentTruckIds.filter((id) => !storedSet.has(id));

  // 削除なし (全 stored id が現存) かつ 追加なし → 内容・順序とも storedIds と同一。
  if (activeIds.length === storedIds.length && newIds.length === 0) {
    return storedIds;
  }
  return [...activeIds, ...newIds];
};

/**
 * 保存済みの表示順 (storedIds = ドラッグで確定した並び) を、現在の車両構成に同期して
 * 「次に保存すべき ids」を解決する。PlanningTrucksPresenter の同期 effect
 * (syncSortedTruckIds) の判断を純粋関数に切り出したもの。setState 不要なら null。
 *
 * - currentTruckIds が空: null（＝同期しない）。
 * - storedIds が空 (未ドラッグ): null。デフォルトソート (compareTrucksByDisplayOrder) に委ねる。
 * - storedIds が有る: reconcileSortedTruckIds で現構成に同期。変化が無ければ null。
 *
 * currentTruckIds が空のときに null を返すのが #2344 の再発防止の要。
 * トラック未ロード / クリア中は truckEntities だけが空になり、表示中の sortedTrucks は
 * effect 側の早期 return で前回値のまま残る。この状態で同期を走らせると、
 *   - storedIds 有 → reconcile が空 truck と突合して storedIds を空にする
 * が発火して sortedTruckIds が空↔前回値を毎レンダー往復し、Maximum update depth に陥る。
 *
 * storedIds が空のとき「sortedTrucks の並びを初期採用する」旧実装は廃止した。
 * デフォルトソートに委ねることで、未ドラッグ状態で車両が追加されたとき末尾ではなく
 * ソート位置に並ぶ (仮車両追加など) 上、不要な初期同期の再レンダーも避けられる。
 */
export const resolveSortedTruckIds = (params: {
  storedIds: number[];
  currentTruckIds: number[];
}): number[] | null => {
  const { storedIds, currentTruckIds } = params;

  // トラック未ロード / クリア中は同期しない (stale 値との突合による無限ループを防ぐ)
  if (currentTruckIds.length === 0) return null;

  if (storedIds.length > 0) {
    const reconciled = reconcileSortedTruckIds(storedIds, currentTruckIds);
    return reconciled === storedIds ? null : reconciled;
  }

  // 未ドラッグ (storedIds 空) はデフォルトソートに委ねる
  return null;
};
